/**
 * Import Service
 * 
 * Serviço para importação de lançamentos com validação e mapeamento
 */

import { supabase } from '../../../lib/supabase';
import type { CashFlowEntry } from '../../../types/database';
import type {
  ExportData,
  ImportOptions,
  ImportResult,
  ImportValidationResult,
  ImportTransactionPreview,
  ExportedTransaction,
} from '../../../types/export-import';
import { generateTransactionHash } from './export.service';
import { transactionCreateSchema } from '../../../lib/schemas/transaction.schema';

/**
 * Valida estrutura do arquivo de importação
 */
export function validateImportFile(file: File): Promise<ExportData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content) as ExportData;

        // Validar estrutura básica
        if (!data.export_metadata) {
          throw new Error('Arquivo inválido: falta export_metadata');
        }
        if (!data.master_data) {
          throw new Error('Arquivo inválido: falta master_data');
        }
        if (!data.transactions || !Array.isArray(data.transactions)) {
          throw new Error('Arquivo inválido: falta transactions ou não é array');
        }

        // Validar versão
        if (data.export_metadata.version !== '1.0') {
          throw new Error(`Versão do arquivo não suportada: ${data.export_metadata.version}`);
        }

        resolve(data);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsText(file);
  });
}

/**
 * Busca transação existente por hash
 */
async function findExistingTransactionByHash(
  hash: string,
  companyId: string
): Promise<CashFlowEntry | null> {
  // Como não temos coluna import_hash no banco, vamos buscar todas as transações
  // da empresa e gerar o hash para comparar (não é o ideal, mas funciona)
  const { data: transactions } = await supabase
    .from('cash_flow_entries')
    .select('*')
    .eq('company_id', companyId);

  if (!transactions) return null;

  for (const transaction of transactions) {
    const existingHash = await generateTransactionHash({
      company_id: transaction.company_id,
      description: transaction.description,
      amount: transaction.amount,
      entry_date: transaction.entry_date,
      due_date: transaction.due_date,
      payment_date: transaction.payment_date,
      invoice_number: transaction.invoice_number,
      category: transaction.category,
      subcategory: transaction.subcategory,
      cost_center: transaction.cost_center,
      payment_method: transaction.payment_method,
      business_partner: transaction.business_partner,
    });

    if (existingHash === hash) {
      return transaction;
    }
  }

  return null;
}

/**
 * Busca ou cria categoria
 */
async function findOrCreateCategory(
  categoryName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  // Buscar existente
  const { data: existing } = await supabase
    .from('categories')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('name', categoryName)
    .single();

  if (existing) {
    return existing.name;
  }

  // Se não auto-criar, retornar null
  if (!autoCreate) {
    return null;
  }

  // Buscar dados do master_data
  const categoryData = masterData.categories.find((c) => c.name === categoryName);

  // Criar nova
  const { data: newCategory, error } = await supabase
    .from('categories')
    .insert({
      company_id: companyId,
      user_id: userId,
      name: categoryName,
      type: categoryData?.type || 'expense',
      description: categoryData?.description || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating category:', error);
    return null;
  }

  return newCategory.name;
}

/**
 * Busca ou cria subcategoria
 */
async function findOrCreateSubcategory(
  subcategoryName: string,
  categoryName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  // Buscar categoria ID
  const { data: category } = await supabase
    .from('categories')
    .select('id')
    .eq('company_id', companyId)
    .eq('name', categoryName)
    .single();

  if (!category) {
    return null;
  }

  // Buscar subcategoria existente
  const { data: existing } = await supabase
    .from('subcategories')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('category_id', category.id)
    .eq('name', subcategoryName)
    .single();

  if (existing) {
    return existing.name;
  }

  // Se não auto-criar, retornar null
  if (!autoCreate) {
    return null;
  }

  // Buscar dados do master_data
  const subcategoryData = masterData.subcategories.find(
    (s) => s.name === subcategoryName && s.category_name === categoryName
  );

  // Criar nova
  const { data: newSubcategory, error } = await supabase
    .from('subcategories')
    .insert({
      company_id: companyId,
      user_id: userId,
      category_id: category.id,
      name: subcategoryName,
      description: subcategoryData?.description || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating subcategory:', error);
    return null;
  }

  return newSubcategory.name;
}

/**
 * Busca ou cria método de pagamento
 */
async function findOrCreatePaymentMethod(
  methodName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('payment_methods')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('name', methodName)
    .single();

  if (existing) return existing.name;
  if (!autoCreate) return null;

  const methodData = masterData.payment_methods.find((m) => m.name === methodName);

  const { data: newMethod, error } = await supabase
    .from('payment_methods')
    .insert({
      company_id: companyId,
      user_id: userId,
      name: methodName,
      method_type: methodData?.method_type || 'outro',
      description: methodData?.description || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating payment method:', error);
    return null;
  }

  return newMethod.name;
}

/**
 * Busca ou cria parceiro comercial
 */
async function findOrCreateBusinessPartner(
  partnerName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('business_partners')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('name', partnerName)
    .single();

  if (existing) return existing.name;
  if (!autoCreate) return null;

  const partnerData = masterData.business_partners.find((p) => p.name === partnerName);

  const { data: newPartner, error } = await supabase
    .from('business_partners')
    .insert({
      company_id: companyId,
      user_id: userId,
      name: partnerName,
      type: partnerData?.type || 'outro',
      document: partnerData?.document || null,
      email: partnerData?.email || null,
      phone: partnerData?.phone || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating business partner:', error);
    return null;
  }

  return newPartner.name;
}

/**
 * Busca ou cria fonte de documento
 */
async function findOrCreateDocumentSource(
  sourceName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('document_sources')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('name', sourceName)
    .single();

  if (existing) return existing.name;
  if (!autoCreate) return null;

  const sourceData = masterData.document_sources.find((s) => s.name === sourceName);

  const { data: newSource, error } = await supabase
    .from('document_sources')
    .insert({
      company_id: companyId,
      user_id: userId,
      name: sourceName,
      type: sourceData?.type || 'outro',
      description: sourceData?.description || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating document source:', error);
    return null;
  }

  return newSource.name;
}

/**
 * Busca ou cria centro de custo
 */
async function findOrCreateCostCenter(
  centerName: string,
  companyId: string,
  userId: string,
  autoCreate: boolean,
  masterData: ExportData['master_data']
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('cost_centers')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('name', centerName)
    .single();

  if (existing) return existing.name;
  if (!autoCreate) return null;

  const centerData = masterData.cost_centers.find((c) => c.name === centerName);

  const { data: newCenter, error } = await supabase
    .from('cost_centers')
    .insert({
      company_id: companyId,
      user_id: userId,
      name: centerName,
      code: centerData?.code || null,
      description: centerData?.description || null,
      active: true,
    })
    .select('name')
    .single();

  if (error) {
    console.error('[ImportService] Error creating cost center:', error);
    return null;
  }

  return newCenter.name;
}

/**
 * Valida transações antes da importação
 */
export async function validateTransactions(
  data: ExportData,
  companyId: string,
  options: ImportOptions
): Promise<ImportValidationResult> {
  const errors: ImportValidationResult['errors'] = [];
  const warnings: string[] = [];
  const missingMasterData: ImportValidationResult['stats']['missing_master_data'] = {
    categories: [],
    payment_methods: [],
    business_partners: [],
    document_sources: [],
    cost_centers: [],
    tags: [],
  };
  
  let validCount = 0;
  let duplicatesCount = 0;

  for (let i = 0; i < data.transactions.length; i++) {
    const transaction = data.transactions[i];
    const line = i + 1;

    try {
      // Validar schema básico
      transactionCreateSchema.parse({
        entry_type: transaction.entry_type,
        description: transaction.description,
        amount: transaction.amount,
        currency: transaction.currency,
        main_category: transaction.main_category,
        category: transaction.category,
        subcategory: transaction.subcategory,
        classification: transaction.classification,
        budget_category: transaction.budget_category,
        payment_status: transaction.payment_status,
      });

      // Verificar duplicata
      if (options.skip_duplicates) {
        const existing = await findExistingTransactionByHash(transaction.import_hash, companyId);
        if (existing) {
          duplicatesCount++;
          warnings.push(`Linha ${line}: Transação duplicada encontrada`);
          continue;
        }
      }

      // Verificar dados mestres faltantes
      if (!missingMasterData.categories.includes(transaction.category)) {
        const { data: category } = await supabase
          .from('categories')
          .select('name')
          .eq('company_id', companyId)
          .eq('name', transaction.category)
          .single();

        if (!category) {
          missingMasterData.categories.push(transaction.category);
          if (!options.auto_create_missing) {
            errors.push({
              line,
              transaction_id: transaction.original_id,
              field: 'category',
              message: `Categoria "${transaction.category}" não encontrada`,
              severity: 'error',
            });
          }
        }
      }

      // Verificar método de pagamento
      if (transaction.payment_method && !missingMasterData.payment_methods.includes(transaction.payment_method)) {
        const { data: method } = await supabase
          .from('payment_methods')
          .select('name')
          .eq('company_id', companyId)
          .eq('name', transaction.payment_method)
          .single();

        if (!method) {
          missingMasterData.payment_methods.push(transaction.payment_method);
          if (!options.auto_create_missing) {
            errors.push({
              line,
              transaction_id: transaction.original_id,
              field: 'payment_method',
              message: `Método de pagamento "${transaction.payment_method}" não encontrado`,
              severity: 'warning',
            });
          }
        }
      }

      validCount++;
    } catch (error) {
      errors.push({
        line,
        transaction_id: transaction.original_id,
        message: error instanceof Error ? error.message : 'Erro desconhecido',
        severity: 'error',
      });
    }
  }

  return {
    is_valid: errors.filter((e) => e.severity === 'error').length === 0,
    errors,
    warnings,
    stats: {
      total_transactions: data.transactions.length,
      valid_transactions: validCount,
      invalid_transactions: errors.filter((e) => e.severity === 'error').length,
      duplicates_found: duplicatesCount,
      missing_master_data: missingMasterData,
    },
  };
}

/**
 * Importa transações
 */
export async function importTransactions(
  data: ExportData,
  companyId: string,
  userId: string,
  options: ImportOptions,
  onProgress?: (progress: { current: number; total: number; message: string }) => void
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    imported: 0,
    skipped: 0,
    errors: 0,
    details: {
      imported_ids: [],
      skipped_transactions: [],
      error_transactions: [],
    },
    created_master_data: {
      categories: 0,
      payment_methods: 0,
      business_partners: 0,
      document_sources: 0,
      cost_centers: 0,
      tags: 0,
    },
  };

  const BATCH_SIZE = 50;
  const total = data.transactions.length;

  for (let i = 0; i < total; i += BATCH_SIZE) {
    const batch = data.transactions.slice(i, i + BATCH_SIZE);

    for (const transaction of batch) {
      try {
        onProgress?.({
          current: i + batch.indexOf(transaction) + 1,
          total,
          message: `Importando lançamento ${i + batch.indexOf(transaction) + 1} de ${total}`,
        });

        // Verificar duplicata
        if (options.skip_duplicates) {
          const existing = await findExistingTransactionByHash(transaction.import_hash, companyId);
          if (existing) {
            result.skipped++;
            result.details.skipped_transactions.push({
              original_id: transaction.original_id,
              reason: 'Duplicata encontrada',
            });
            continue;
          }
        }

        // Mapear relacionamentos
        const category = transaction.category
          ? await findOrCreateCategory(
              transaction.category,
              companyId,
              userId,
              options.auto_create_missing,
              data.master_data
            )
          : null;

        const subcategory =
          transaction.subcategory && category
            ? await findOrCreateSubcategory(
                transaction.subcategory,
                category,
                companyId,
                userId,
                options.auto_create_missing,
                data.master_data
              )
            : null;

        const paymentMethod = transaction.payment_method
          ? await findOrCreatePaymentMethod(
              transaction.payment_method,
              companyId,
              userId,
              options.auto_create_missing,
              data.master_data
            )
          : null;

        const businessPartner = transaction.business_partner
          ? await findOrCreateBusinessPartner(
              transaction.business_partner,
              companyId,
              userId,
              options.auto_create_missing,
              data.master_data
            )
          : null;

        const documentSource = transaction.document_source
          ? await findOrCreateDocumentSource(
              transaction.document_source,
              companyId,
              userId,
              options.auto_create_missing,
              data.master_data
            )
          : null;

        const costCenter = transaction.cost_center
          ? await findOrCreateCostCenter(
              transaction.cost_center,
              companyId,
              userId,
              options.auto_create_missing,
              data.master_data
            )
          : null;

        // Criar lançamento
        const { data: newTransaction, error } = await supabase
          .from('cash_flow_entries')
          .insert({
            company_id: companyId,
            user_id: userId,
            entry_type: transaction.entry_type,
            description: transaction.description,
            notes: transaction.notes,
            amount: transaction.amount,
            currency: transaction.currency,
            main_category: transaction.main_category,
            category: category || transaction.category,
            subcategory: subcategory,
            classification: transaction.classification,
            budget_category: transaction.budget_category,
            cost_center: costCenter,
            expense_type: transaction.expense_type,
            entry_date: transaction.entry_date,
            due_date: transaction.due_date,
            payment_date: transaction.payment_date,
            is_recurring: false, // Não importar como recorrente para evitar duplicações
            frequency: null,
            recurrence_end_date: null,
            recurrence_group_id: null,
            payment_status: transaction.payment_status,
            payment_method: paymentMethod,
            account_name: transaction.account_name,
            payment_details: transaction.payment_details,
            invoice_number: transaction.invoice_number,
            tax_category: transaction.tax_category,
            business_partner: businessPartner,
            location: transaction.location,
            stakeholders: transaction.stakeholders,
            document_source: documentSource,
            tags: transaction.tags,
            is_simulation: transaction.is_simulation,
          })
          .select('id')
          .single();

        if (error) {
          throw error;
        }

        result.imported++;
        result.details.imported_ids.push(newTransaction.id);
      } catch (error) {
        result.errors++;
        result.details.error_transactions.push({
          original_id: transaction.original_id,
          error: error instanceof Error ? error.message : 'Erro desconhecido',
        });
      }
    }
  }

  result.success = result.errors === 0;
  return result;
}

